import { useId } from "react";

/**
 * The beer in the glass its style is poured in (Tyler, 2026-10-03: "a blonde ale should be a pint
 * glass, a stout should be a stout glass ... A marzen/oktoberfest could be a little easter egg in a
 * boot, pilsner in a tall pils glass"), filled with the color the recipe works out to, which comes in
 * as `--beer`. The glasses follow the usual pairings (KegWorks' glassware guide among them); a style
 * the list does not know is poured in a shaker pint.
 */

export type Glass =
  | "shaker"
  | "nonic"
  | "pilsner"
  | "weizen"
  | "stange"
  | "snifter"
  | "stout"
  | "goblet"
  | "tulip"
  | "ipa"
  | "mug"
  | "boot";

/**
 * First match wins, so the narrower names come before the broader ones they contain. The GABF guide
 * spells Märzen and Kölsch "Maerzen" and "Koelsch", so both spellings count.
 */
const BY_STYLE: [RegExp, Glass][] = [
  [/m(a|ä|ae)rzen|oktoberfest|wiesn|festbier/i, "boot"],
  [/pils|czech-style pale lager/i, "pilsner"],
  [/k(o|ö|oe)lsch|altbier|\balt\b|gose|lambic|gueuze|berliner|grodzisk/i, "stange"],
  [
    /barley ?wine|wheat ?wine|imperial stout|russian|wee heavy|old ale|eisbock|\bstrong (ale|beer)\b/i,
    "snifter",
  ],
  [/stout|porter/i, "stout"],
  [/weizen|weiss|weisse|hefe|wheat|witbier|\bwit\b/i, "weizen"],
  [/dubbel|tripel|quadrupel|\bquad\b|trappist|abbey|dark strong/i, "goblet"],
  [/india pale|\bipa\b|hazy|juicy|new england/i, "ipa"],
  [/bitter|\bmild\b|english-style|british-style|irish-style|scottish-style/i, "nonic"],
  [/saison|farmhouse|bi[eè]re de garde|scotch|flanders|oud bruin|belgian/i, "tulip"],
  [/lager|helles|dunkel|bock|vienna|schwarz|keller|zwickel|rauch|dortmund|export/i, "mug"],
];

/** The glass a style is poured in. */
export function glassFor(style: string | null | undefined): Glass {
  if (!style) return "shaker";
  return BY_STYLE.find(([words]) => words.test(style))?.[1] ?? "shaker";
}

export const GLASS_NAMES: Record<Glass, string> = {
  shaker: "a pint glass",
  nonic: "a nonic pint",
  pilsner: "a pilsner glass",
  weizen: "a weizen glass",
  stange: "a stange",
  snifter: "a snifter",
  stout: "a stout glass",
  goblet: "a goblet",
  tulip: "a tulip",
  ipa: "an IPA glass",
  mug: "a mug",
  boot: "a boot",
};

interface Shape {
  /** The inside of the glass, which the beer fills. */
  inside: string;
  /** Where the foam starts and where the beer below it starts. */
  foam: number;
  beer: number;
  /** Solid glass: a stem, a foot, a handle, a ribbed base. */
  solid?: string;
  /** Lines drawn on the glass, such as ribs or facets. */
  lines?: string;
  /** A handle, drawn heavier. */
  handle?: string;
}

/** Each glass in a 60 by 80 box, standing on its bottom edge. */
const SHAPES: Record<Glass, Shape> = {
  shaker: { inside: "M10 6 L50 6 L44 76 L16 76 Z", foam: 6, beer: 15 },
  nonic: {
    inside: "M11 6 L49 6 L48 11 C51 15 51 20 48.5 24 L44 76 L16 76 L11.5 24 C9 20 9 15 12 11 Z",
    foam: 6,
    beer: 14,
  },
  pilsner: {
    inside: "M15 2 L45 2 L35 64 L25 64 Z",
    foam: 2,
    beer: 12,
    solid: "M25 64 L35 64 L39 76 L21 76 Z",
  },
  weizen: {
    inside:
      "M17 2 C13 12 12 22 16 34 C19 46 22 58 23 76 L37 76 C38 58 41 46 44 34 C48 22 47 12 43 2 Z",
    foam: 2,
    beer: 15,
  },
  stange: { inside: "M20 4 L40 4 L40 76 L20 76 Z", foam: 4, beer: 12 },
  snifter: {
    inside: "M20 12 C8 20 6 37 14 46 C20 52 40 52 46 46 C54 37 52 20 40 12 Z",
    foam: 28,
    beer: 31,
    solid: "M28 51 L32 51 L32 70 L28 70 Z M17 70 L43 70 L45 76 L15 76 Z",
  },
  stout: {
    inside:
      "M14 4 C12 14 8 22 10 34 C12 46 17 60 18 76 L42 76 C43 60 48 46 50 34 C52 22 48 14 46 4 Z",
    foam: 4,
    beer: 13,
  },
  goblet: {
    inside: "M7 10 L53 10 C53 30 45 43 30 43 C15 43 7 30 7 10 Z",
    foam: 10,
    beer: 16,
    solid: "M27 42 L33 42 L33 68 L27 68 Z M15 68 L45 68 L47 76 L13 76 Z",
  },
  tulip: {
    inside: "M17 4 C12 12 10 24 14 34 C18 44 24 48 30 48 C36 48 42 44 46 34 C50 24 48 12 43 4 Z",
    foam: 4,
    beer: 12,
    solid: "M28 47 L32 47 L32 70 L28 70 Z M17 70 L43 70 L45 76 L15 76 Z",
  },
  ipa: {
    inside: "M14 4 C12 16 14 28 20 38 L24 46 L36 46 L40 38 C46 28 48 16 46 4 Z",
    foam: 4,
    beer: 12,
    solid: "M24 46 L36 46 L39 76 L21 76 Z",
    lines: "M23 52 L37 52 M22.5 58 L37.5 58 M22 64 L38 64 M21.5 70 L38.5 70",
  },
  mug: {
    inside: "M8 10 L42 10 L42 76 L8 76 Z",
    foam: 10,
    beer: 19,
    lines: "M16 24 L16 66 M25 24 L25 66 M34 24 L34 66",
    handle: "M42 22 C57 22 57 60 42 60",
  },
  boot: {
    inside: "M14 2 L38 2 L38 48 C38 58 44 62 53 64 C59 66 58 76 51 76 L14 76 Z",
    foam: 2,
    beer: 11,
  },
};

export function BeerGlass({
  style,
  title,
  className = "beer-glass",
}: {
  style: string | null | undefined;
  title?: string;
  className?: string;
}) {
  const id = useId();
  const clip = `${id}-inside`;
  const shine = `${id}-shine`;
  const glass = glassFor(style);
  const s = SHAPES[glass];
  return (
    <svg
      className={className}
      viewBox="0 0 60 80"
      role="img"
      aria-label={title ? `${title}, in ${GLASS_NAMES[glass]}` : GLASS_NAMES[glass]}
      data-glass={glass}
    >
      {title ? <title>{`${title}, in ${GLASS_NAMES[glass]}`}</title> : null}
      <defs>
        <clipPath id={clip}>
          <path d={s.inside} />
        </clipPath>
        <linearGradient id={shine} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0.32" />
          <stop offset="0.35" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      {s.solid ? <path className="glass-solid" d={s.solid} /> : null}
      <g clipPath={`url(#${clip})`}>
        <rect className="glass-beer" x="0" y={s.beer} width="60" height="80" />
        <rect className="glass-foam" x="0" y={s.foam} width="60" height={s.beer - s.foam} />
        <rect x="0" y="0" width="60" height="80" fill={`url(#${shine})`} />
      </g>
      <path className="glass-edge" d={s.inside} />
      {s.lines ? <path className="glass-lines" d={s.lines} /> : null}
      {s.handle ? <path className="glass-handle" d={s.handle} /> : null}
    </svg>
  );
}
